import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Day } from '../../../Calendar/components/Day';
import { Image } from '../../../Image';
import dayjs from 'dayjs';
import { COLORS, FONTS } from '../../../../constants';
import DropDownSelect from '../../../DropDownSelect';

export interface CalendarRangeProps {
  checkinDay: string;
  checkoutDay: string;
  onChangeCheckinDay: (day: string) => void;
  onChangeCheckoutDay: (day: string) => void;
  showCalendar: boolean;
  setShowCalendar: (showCalendar: boolean) => void;
  isSelectCheckin: boolean;
  setIsSelectCheckin: (isSelectCheckin: boolean) => void;
  beginYear?: number;
  isRange?: boolean;
}

const CalendarRange: FC<CalendarRangeProps> = ({
  checkinDay,
  checkoutDay,
  onChangeCheckinDay,
  onChangeCheckoutDay,
  showCalendar = false,
  setShowCalendar,
  beginYear = 1950,
  isRange = false,
  isSelectCheckin,
  setIsSelectCheckin,
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(
    new Date(checkinDay || dayjs(new Date()).format('YYYY-MM-DD'))
  );
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    return lastDay;
  };

  const handleDateSelect = useCallback(
    (date: string) => {
      if (isRange) {
        const selectCheckin = isSelectCheckin
          ? new Date(date)
          : new Date(checkinDay);
        const selectCheckout = !isSelectCheckin
          ? new Date(date)
          : new Date(checkoutDay);
        if (isSelectCheckin) {
          onChangeCheckinDay(date);
          if (selectCheckin >= selectCheckout) {
            selectCheckout.setDate(selectCheckin.getDate() + 1);
            onChangeCheckoutDay(dayjs(selectCheckout).format('YYYY-MM-DD'));
          }
        } else {
          onChangeCheckoutDay(date);
          if (selectCheckin >= selectCheckout) {
            selectCheckin.setDate(selectCheckout.getDate() - 1);
            onChangeCheckinDay(dayjs(selectCheckin).format('YYYY-MM-DD'));
          }
        }
        setIsSelectCheckin(!isSelectCheckin);
      } else {
        onChangeCheckinDay(date);
        onChangeCheckoutDay(date);
      }
    },
    [
      checkinDay,
      checkoutDay,
      isRange,
      isSelectCheckin,
      onChangeCheckinDay,
      onChangeCheckoutDay,
      setIsSelectCheckin,
    ]
  );

  const renderCalendar = useMemo(() => {
    let checkInDay = new Date(checkinDay);
    let checkOutDay = new Date(checkoutDay);

    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );
    const startingDayOfWeek = firstDayOfMonth.getDay();

    const calendarDays = [];
    let weekDays = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      weekDays.push(<Day emptyDate key={`empty-day-${i}`} />);
    }

    for (let i = 1; i <= 7 - startingDayOfWeek; i++) {
      weekDays.push(
        <Day
          key={`calendar-day-${i}`}
          onPress={handleDateSelect}
          date={i}
          initDate={dayjs(checkInDay).format('YYYY-MM-DD')}
          current={currentDate}
          beginDate={checkInDay}
          endDate={checkOutDay}
        />
      );
    }
    calendarDays.push(
      <View style={styles.calendarRow} key={`row-${0}`}>
        {weekDays}
      </View>
    );
    weekDays = [];
    let count = 0;
    for (let i = 7 - startingDayOfWeek + 1; i <= daysInMonth; i++) {
      weekDays.push(
        <Day
          key={`calendar-day-${i}`}
          onPress={handleDateSelect}
          date={i}
          current={currentDate}
          initDate={dayjs(checkInDay).format('YYYY-MM-DD')}
          beginDate={checkInDay}
          endDate={checkOutDay}
        />
      );
      count++;
      if (count === 7) {
        calendarDays.push(
          <View style={styles.calendarRow} key={`row-${i}`}>
            {weekDays}
          </View>
        );
        weekDays = [];
        count = 0;
      }
    }
    if (weekDays.length > 0) {
      const length = 7 - weekDays.length;
      for (let i = 0; i < length; i++) {
        weekDays.push(<Day emptyDate key={`empty-day-${i}-end`} />);
      }
      calendarDays.push(
        <View style={styles.calendarRow} key={`row-end`}>
          {weekDays}
        </View>
      );
    }
    return <View style={styles.calendarContainer}>{calendarDays}</View>;
  }, [handleDateSelect, checkinDay, checkoutDay, currentDate]);

  const handlePreviousMonth = () => {
    const previousMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1
    );
    setCurrentDate(previousMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1
    );
    setCurrentDate(nextMonth);
  };

  const handlePreviousYear = () => {
    const previousYear = new Date(
      currentDate.getFullYear() - 1,
      currentDate.getMonth()
    );
    setCurrentDate(previousYear);
  };

  const handleNextYear = () => {
    const nextYear = new Date(
      currentDate.getFullYear() + 1,
      currentDate.getMonth()
    );
    setCurrentDate(nextYear);
  };

  const handleSelectYear = (year: number) => {
    const newYear = new Date(year, currentDate.getMonth());
    setCurrentDate(newYear);
  };

  const daysOfWeek = useMemo(() => {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }, []);

  const yearItems = useMemo(() => {
    const years = [];
    for (let year = beginYear; year <= 2050; year++) {
      years.push({ label: year.toString(), value: year });
    }
    return years;
  }, [beginYear]);

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
        }}
        onPress={() => {
          if (setShowCalendar) {
            setShowCalendar(false);
          }
        }}
      >
        <Image
          style={{
            width: 18,
            height: 18,
            tintColor: COLORS.BLACK,
          }}
          source={require('../../../../assets/close-icon.png')}
        />
      </TouchableOpacity>
      <View style={styles.selectWrapper}>
        <DropDownSelect
          value={currentDate.getFullYear()}
          setValues={(year) => {
            handleSelectYear(Number(year));
          }}
          items={yearItems}
          style={{
            width: 150,
            marginBottom: 5,
          }}
          dropdownStyle={{
            borderWidth: 0,
            paddingTop: 0,
            paddingBottom: 0,
            paddingStart: 0,
            paddingEnd: 0,
          }}
          textStyle={{
            color: COLORS.BLACK,
            textAlign: 'center',
            fontWeight: '700',
            fontFamily: FONTS.Arial,
            fontSize: 16,
            lineHeight: 28,
            letterSpacing: 2,
            textTransform: 'uppercase',
            paddingHorizontal: 10,
          }}
          buttonStyle={{
            display: 'none',
          }}
          dropDownContainerStyled={{ top: 25 }}
          title={'Please select year'}
          placeholder={'Select year'}
        />
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <TouchableOpacity
            style={[styles.arrowButton]}
            onPress={handlePreviousMonth}
          >
            <Image
              source={require('../../../../assets/left-arrow.png')}
              style={styles.arrowStyle}
            />
          </TouchableOpacity>
          <Text style={styles.currentDate}>
            {dayjs(currentDate).format('MMMM')}
          </Text>
          <TouchableOpacity
            style={styles.arrowButton}
            onPress={handleNextMonth}
          >
            <Image
              source={require('../../../../assets/right-arrow.png')}
              style={styles.arrowStyle}
            />
          </TouchableOpacity>
        </View>
      </View>
      <View
        style={{ flexDirection: 'row', paddingVertical: 10, width: '100%' }}
      >
        {daysOfWeek.map((day) => (
          <View style={{ flex: 1 }} key={day}>
            <Text
              style={[
                styles.dayTitle,
                (day == 'Sun' || day == 'Sat') && { color: '#F36A6A' },
              ]}
            >
              {day}
            </Text>
          </View>
        ))}
      </View>
      <View style={{ width: '100%' }}>{renderCalendar}</View>
    </View>
  );
};

export default CalendarRange;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 5,
    paddingTop: 15,
  },
  markedPeriod: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    height: 50,
  },
  arrowButton: {
    padding: 1,
    width: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  arrowStyle: {
    height: 18,
    width: 10,
    // resizeMode: 'contain',
    tintColor: COLORS.BLACK,
  },
  arrowButtonVer: {
    padding: 2,
  },
  arrowStyleVer: {
    height: 14,
    width: 13,
    resizeMode: 'contain',
    tintColor: COLORS.BLACK,
  },
  monthButton: {
    fontSize: 20,
    fontWeight: 'bold',
    marginHorizontal: 10,
  },
  monthText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  calendarContainer: {
    flexDirection: 'column',
  },
  calendarRow: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  dayButton: {
    flex: 1,
    height: 40,
    padding: 4,
    borderRadius: 1,
    backgroundColor: COLORS.LIGHT_GREY,
  },
  dayText: {
    fontSize: 13,
    fontFamily: FONTS.AvenirMedium,
    textTransform: 'uppercase',
    color: COLORS.DARKER_GREY,
  },
  dayOfWeekText: {
    fontSize: 12,
  },
  emptyDay: {
    width: 40,
    height: 100,
    margin: 5,
  },
  dayTitle: {
    fontFamily: FONTS.AvenirMedium,
    fontSize: 13,
    textAlign: 'center',
    color: COLORS.BLACK,
  },
  selectWrapper: {
    flexDirection: 'column',
    zIndex: 100,
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  currentDate: {
    color: COLORS.BLACK,
    fontWeight: '700',
    fontFamily: FONTS.Arial,
    fontSize: 16,
    lineHeight: 28,
    letterSpacing: 2,
    textTransform: 'uppercase',
    paddingHorizontal: 10,
    width: 138,
    textAlign: 'center',
  },
  currentYear: {
    color: COLORS.BLACK,
    fontWeight: '700',
    fontFamily: FONTS.Arial,
    fontSize: 16,
    lineHeight: 28,
    letterSpacing: 2,
    textTransform: 'uppercase',
    paddingHorizontal: 10,
    width: 138,
    textAlign: 'center',
  },
});
